import React, { useEffect, useMemo, useState } from "react";
import AuthGate from "./AuthGate";
import ClientsView from "./ClientsView";
import CliniciansView from "./CliniciansView";
import { supabase } from "./supabaseClient";

function usePath() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (to) => {
    if (to === window.location.pathname) return;
    window.history.pushState({}, "", to);
    setPath(to);
  };

  return { path, navigate };
}

function TopNav({ active, onNavigate, onSignOut }) {
  const tab = (key, label) => (
    <button className={`cp-tab ${active === key ? "active" : ""}`} onClick={() => onNavigate(key)}>
      {label}
    </button>
  );

  return (
    <div className="cp-topbar">
      <img src="/carepathwell.png" alt="CarePathWell" height={28} />
      <div className="cp-tabs">
        {tab("clients", "Clients")}
        {tab("clinicians", "Clinicians")}
      </div>
      <button className="cp-btn danger" onClick={onSignOut}>Sign out</button>
    </div>
  );
}

function AppShell() {
  const { path, navigate } = usePath();

  const activeTab = useMemo(() => (path.startsWith("/clinicians") ? "clinicians" : "clients"), [path]);

  useEffect(() => {
    if (path.startsWith("/auth/callback")) {
      window.history.replaceState({}, "", "/");
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const onNavigate = (tab) => navigate(tab === "clinicians" ? "/clinicians" : "/");

  const onSignOut = async () => {
  await supabase.auth.signOut();
  setOrgId(null);
  setClients([]);
  setClinicians([]);
  setAssignments([]);
  window.history.replaceState({}, "", "/");
  navigate("/");
};


  // ---- org_id for the signed-in user (from org_members) ----
  const [orgId, setOrgId] = useState(null);

  // app data
  const [clients, setClients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [loading, setLoading] = useState(true);

  async function loadAll() {
    setLoading(true);

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      setOrgId(null);
      setClients([]);
      setClinicians([]);
      setAssignments([]);
      setLoading(false);
      return;
    }

    // fetch org
    const { data: member, error: mErr } = await supabase
      .from("org_members")
      .select("org_id, approved")
      .eq("user_id", userId)
      .maybeSingle();

    if (mErr || !member?.approved) {
      // AuthGate should prevent this, but keep safe.
      setOrgId(null);
      setClients([]);
      setClinicians([]);
      setAssignments([]);
      setLoading(false);
      return;
    }

    setOrgId(member.org_id);

    const [cRes, clRes, aRes] = await Promise.all([
      supabase.from("clients").select("*").eq("org_id", member.org_id).order("created_at", { ascending: false }),
      supabase.from("clinicians").select("*").eq("org_id", member.org_id).order("created_at", { ascending: false }),
      supabase.from("assignments").select("*").eq("org_id", member.org_id),
    ]);

    if (cRes.error) console.error("clients load error:", cRes.error);
    if (clRes.error) console.error("clinicians load error:", clRes.error);
    if (aRes.error) console.error("assignments load error:", aRes.error);

    setClients(Array.isArray(cRes.data) ? cRes.data : []);
    setClinicians(Array.isArray(clRes.data) ? clRes.data : []);
    setAssignments(Array.isArray(aRes.data) ? aRes.data : []);

    setLoading(false);
  }

  useEffect(() => {
    loadAll();
    const { data: sub } = supabase.auth.onAuthStateChange(() => loadAll());
    return () => sub?.subscription?.unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- API used by views ----
  const api = useMemo(() => {
    return {
      addClient: async (payload) => {
        if (!orgId) return null;
        const { data, error } = await supabase
          .from("clients")
          .insert([{ ...payload, org_id: orgId }])
          .select("*")
          .single();
        if (error) throw error;
        setClients((prev) => [data, ...prev]);
        return data;
      },

      updateClient: async (id, patch) => {
        const { data, error } = await supabase
          .from("clients")
          .update(patch)
          .eq("id", id)
          .select("*")
          .single();
        if (error) throw error;
        setClients((prev) => prev.map((c) => (c.id === id ? data : c)));
      },

      deleteClient: async (id) => {
        const { error } = await supabase.from("clients").delete().eq("id", id);
        if (error) throw error;
        setClients((prev) => prev.filter((c) => c.id !== id));
        setAssignments((prev) => prev.filter((a) => a.client_id !== id));
      },

      addClinician: async (payload) => {
        if (!orgId) return null;
        const { data, error } = await supabase
          .from("clinicians")
          .insert([{ ...payload, org_id: orgId }])
          .select("*")
          .single();
        if (error) throw error;
        setClinicians((prev) => [data, ...prev]);
        return data;
      },

      deleteClinician: async (id) => {
        const { error } = await supabase.from("clinicians").delete().eq("id", id);
        if (error) throw error;
        setClinicians((prev) => prev.filter((c) => c.id !== id));
        setAssignments((prev) => prev.filter((a) => a.clinician_id !== id));
      },

      assignClient: async (clientId, clinicianId) => {
        if (!orgId) return;

        // upsert unique(org_id, client_id)
        const { data, error } = await supabase
          .from("assignments")
          .upsert([{ org_id: orgId, client_id: clientId, clinician_id: clinicianId }], {
            onConflict: "org_id,client_id",
          })
          .select("*")
          .single();

        if (error) throw error;

        setAssignments((prev) => {
          const next = prev.filter((a) => a.client_id !== clientId);
          next.push(data);
          return next;
        });
      },

      unassignClient: async (clientId) => {
        if (!orgId) return;
        const { error } = await supabase
          .from("assignments")
          .delete()
          .eq("org_id", orgId)
          .eq("client_id", clientId);
        if (error) throw error;
        setAssignments((prev) => prev.filter((a) => a.client_id !== clientId));
      },
    };
  }, [orgId]);

  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-card" style={{ maxWidth: 780, margin: "24px auto" }}>
          <h3>Loading data…</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-app">
      <TopNav active={activeTab} onNavigate={onNavigate} onSignOut={onSignOut} />
      <main className="cp-page">
        {activeTab === "clinicians" ? (
          <CliniciansView clinicians={clinicians} clients={clients} assignments={assignments} api={api} />
        ) : (
          <ClientsView clients={clients} clinicians={clinicians} assignments={assignments} api={api} />
        )}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthGate>
      <AppShell />
    </AuthGate>
  );
}
